import React from 'react'
import cx from 'classnames'
import * as R from 'ramda'

import Icon from 'components/common/Icon'
import TetherPortal from 'components/common/TetherPortal'

import styles from './Dropdown.module.scss'

export const DropdownContext = React.createContext<(() => void) | undefined>(
  undefined,
)

type AnchorProps = {
  className?: string
  isActive?: boolean
  onClick?: React.MouseEventHandler<HTMLElement>
}

export const ChevronAnchor = React.forwardRef<HTMLSpanElement, AnchorProps>(
  ({ className, isActive, ...props }, ref) => (
    <span
      className={cx(
        styles.anchor,
        {
          [styles.active]: isActive,
        },
        className,
      )}
      ref={ref}
      {...props}
    >
      <Icon className={styles.chevron} name="chevronDown" />
    </span>
  ),
)

type DropdownProps = {
  Anchor?: React.ComponentType<AnchorProps & React.RefAttributes<HTMLElement>>
  children?: React.ReactNode
  className?: string
  focus?: boolean
  order?: string
}

export const Dropdown = ({
  Anchor = ChevronAnchor,
  children,
  className,
  focus,
  order = 'bottom top right left center',
}: DropdownProps) => {
  const ref = React.useRef<HTMLElement>(null)

  const [isOpen, setOpen] = React.useState(focus)

  const toggle = React.useCallback(() => setOpen(R.not), [])
  const dismiss = React.useCallback(() => setOpen(false), [])

  return (
    <DropdownContext.Provider value={dismiss}>
      <Anchor
        className={className}
        ref={ref}
        onClick={toggle}
        isActive={isOpen}
      />
      <TetherPortal
        anchorRef={ref}
        order={order}
        dismiss={dismiss}
        open={isOpen}
      >
        <div className={styles.content}>{children}</div>
      </TetherPortal>
    </DropdownContext.Provider>
  )
}
